import { Component, computed, DestroyRef, effect, inject, input, QueryList, signal, ViewChildren } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { faClose, faUpRightAndDownLeftFromCenter, faInfoCircle } from '@fortawesome/pro-regular-svg-icons';

import { filter, fromEvent } from 'rxjs';
import { default as SwiperCore, Pagination, SwiperOptions } from 'swiper';
import { PaginationOptions } from 'swiper/types';

import { MediaService } from '@eiq/files';
import { Media } from '@eiq/files/models';

import { FileType } from '@empathy/global/data';

import { SurveyViewerStateService } from '../../services/survey-viewer-state.service';
import { MediaViewComponent } from '../media-view/media-view.component';

SwiperCore.use([Pagination]);

@Component({
  selector: 'empathy-stimulus',
  templateUrl: 'stimulus.component.html',
  styleUrls: ['./stimulus.component.scss']
})
export class StimulusComponent {
  @ViewChildren(MediaViewComponent) mediaViewComponents!: QueryList<MediaViewComponent>;

  expandable = input<boolean>(true);
  stimulus = input([], {
    transform: (value: Media[] | null) => (!value || Object.keys(value).length === 0 ? [] : value)
  });
  singleImageSize = input<'medium' | 'large'>('medium');
  autoplay = input<boolean>(false);

  mediaRequiresCompletion$ = signal<boolean>(false);

  expandedMedia$ = signal<Media>(null);

  stimulusView$ = computed<Media[]>(() => {
    const urls = this.resolvedUrls$();
    return this.stimulus().map((s) => (s.url || !urls[s.mediaId] ? s : { ...s, url: urls[s.mediaId] }));
  });

  private resolvedUrls$ = signal<Record<string, string>>({});

  swiperConfig: SwiperOptions = {
    initialSlide: 0,
    spaceBetween: 8,
    centeredSlides: false,
    centeredSlidesBounds: true,
    grabCursor: true,
    pagination: {
      clickable: true
    },
    breakpointsBase: 'container',
    slidesPerView: 'auto'
  };

  swiperPaginationConfig: PaginationOptions = {
    clickable: true,
    el: '.swiper-pagination'
  };

  faClose = faClose;
  faUpRightAndDownLeftFromCenter = faUpRightAndDownLeftFromCenter;
  faInfoCircle = faInfoCircle;
  fileType = FileType;

  surveyViewerStateService = inject(SurveyViewerStateService);

  private destroyRef = inject(DestroyRef);

  constructor(private mediaService: MediaService) {
    fromEvent<KeyboardEvent>(document, 'keydown')
      .pipe(
        filter((event) => event.key === 'Escape'),
        filter(() => this.expandedMedia$() !== null),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.expandedMedia$.set(null);
      });

    effect(
      () => {
        const mediaIds = this.stimulus().map((m) => m.mediaId);
        this.mediaRequiresCompletion$.set(mediaIds.some((id) => this.surveyViewerStateService.mediaRequiresCompletion(id)));
      },
      { allowSignalWrites: true }
    );

    effect(
      () => {
        this.stimulus()
          .filter((s) => !s.url && !this.resolvedUrls$()[s.mediaId])
          .forEach((s) =>
            this.mediaService.getMediaUrl(s.mediaId).subscribe((media) => this.resolvedUrls$.update((urls) => ({ ...urls, [s.mediaId]: media.url })))
          );
      },
      { allowSignalWrites: true }
    );
  }

  onExpandMedia(stimulus: Media, url: string, index: number): void {
    if (stimulus.type === FileType.VideoMp4) {
      this.mediaViewComponents.get(index)?.pause();
    }

    this.expandedMedia$.set({ ...stimulus, url });
  }
}
